import { FC, useMemo } from "react";
import { ServiceAreaNestingParentDropdownItem } from "@app/report-customization/manage-presets/interfaces/serviceAreaNestingParentDropdownItem";
import { SpecialtyGroupWithSpecialtiesModel } from "@app/shared/interfaces/specialtyGroupWithSpecialtiesModel";
import { FilterChipDescriptor, QesActiveFiltersBar } from "@app/shared/QesActiveFiltersBar";
import { getDisplayProviderType } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityFilterHelper";
import { formatSpecialtyWithCode, getFullySelectedGroupCodes, groupSpecialtyKey, isSingleCodeGroup } from "@app/projects/network-assessment/network-integrity/helpers/specialtyKeyHelpers";
import { NetworkIntegrityActionStatus, NetworkIntegrityActionStatusDisplay, NetworkIntegrityMembership } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { toNpiTypeLabel } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityNpiTypeHelper";
import { EntityTypeEnum, RegionSetStatusType } from "@app/projects/enumerators";
import { SERVICE_AREA_STATUS_DISPLAY_ORDER } from "@app/projects/network-assessment/serviceAreaStatusConstants";

interface NetworkIntegrityAppliedFiltersProps {
  pendingActionStatus: NetworkIntegrityActionStatus;
  readonly pendingMembership: string;
  readonly pendingNpiType?: EntityTypeEnum | null;
  pendingProviderType: string;
  readonly pendingRegionSetIds: readonly number[];
  readonly pendingSpecialtyCodes: readonly string[];
  setPendingActionStatus: (value: NetworkIntegrityActionStatus) => void;
  setPendingNpiType?: (value: EntityTypeEnum | null) => void;
  setPendingRegionSetIds: (ids: number[]) => void;
  setPendingSpecialtyCodes: (codes: string[]) => void;
  readonly serviceAreaOptions: readonly ServiceAreaNestingParentDropdownItem[];
  readonly specialtyGroups: readonly SpecialtyGroupWithSpecialtiesModel[];
  readonly statusToRegionSet: Map<RegionSetStatusType, Set<number>>;
  hasDualMembershipProviders: boolean;
  isDualPrimaryMembershipSelected: boolean;
  primaryMembershipAlias: string | null;
  secondaryMembershipAlias: string | null;
  excessWarning: string | null;
}

const MAX_PILL_TEXT_LENGTH = 20;

export const NetworkIntegrityAppliedFilters: FC<NetworkIntegrityAppliedFiltersProps> = ({
  excessWarning,
  hasDualMembershipProviders,
  isDualPrimaryMembershipSelected,
  pendingActionStatus,
  pendingMembership,
  pendingNpiType,
  pendingProviderType,
  pendingRegionSetIds,
  pendingSpecialtyCodes,
  primaryMembershipAlias,
  secondaryMembershipAlias,
  serviceAreaOptions,
  setPendingActionStatus,
  setPendingNpiType,
  setPendingRegionSetIds,
  setPendingSpecialtyCodes,
  specialtyGroups,
  statusToRegionSet,
}) => {
  const chips = useMemo<FilterChipDescriptor[]>(() => {
    const result: FilterChipDescriptor[] = [];

    if (pendingProviderType) {
      const viewText = getDisplayProviderType(
        pendingProviderType
      );
      if (viewText) {
        result.push({ content: viewText, group: "View", key: "view", truncateAt: MAX_PILL_TEXT_LENGTH });
      }
    }

    if (pendingMembership && hasDualMembershipProviders){
      let membershipText = pendingMembership;
      if(membershipText === NetworkIntegrityMembership.Primary)
        membershipText = primaryMembershipAlias || "Primary Membership";
      else if (membershipText === NetworkIntegrityMembership.Secondary)
        membershipText = secondaryMembershipAlias || "Secondary Membership";

      result.push({ content: membershipText, group: "Membership", key: "membership", truncateAt: MAX_PILL_TEXT_LENGTH });
    }

    if (excessWarning) {
      result.push({ content: excessWarning, group: "Excess Warning", key: "excess-warning", truncateAt: MAX_PILL_TEXT_LENGTH });
    }

    // Service Area — grouped by county status
    const regionSetIdToStatus = new Map<number, RegionSetStatusType>();
    for (const [status, ids] of statusToRegionSet) {
      for (const id of ids) regionSetIdToStatus.set(id, status);
    }

    const countyChips: FilterChipDescriptor[] = [];
    for (const id of pendingRegionSetIds) {
      let name = String(id);
      for (const sa of serviceAreaOptions) {
        const found = (sa.children ?? []).find(c => Number(c.key) === id);
        if (found) { name = `${found.name}, ${sa.name}`; break; }
      }
      const status = regionSetIdToStatus.get(id);
      countyChips.push({
        content: name,
        group: "Service Area",
        key: `service-area-${id}`,
        onDismiss: () => setPendingRegionSetIds(pendingRegionSetIds.filter(r => r !== id)),
        subgroup: status ? `${status} Counties` : undefined,
        truncateAt: MAX_PILL_TEXT_LENGTH,
      });
    }
    countyChips.sort((a, b) => {
      const order = (subgroup: string | undefined) => {
        const idx = SERVICE_AREA_STATUS_DISPLAY_ORDER.findIndex(s => `${s} Counties` === subgroup);
        return idx === -1 ? SERVICE_AREA_STATUS_DISPLAY_ORDER.length : idx;
      };
      return order(a.subgroup) - order(b.subgroup);
    });
    result.push(...countyChips);

    if (pendingSpecialtyCodes.length > 0) {
      const fullySelectedGroups = getFullySelectedGroupCodes(specialtyGroups, pendingSpecialtyCodes);
      const selectedKeys = new Set(pendingSpecialtyCodes);
      let parentIdx = 0;
      let childIdx = 0;

      for (const group of specialtyGroups) {
        const collapsed = isSingleCodeGroup(group);
        if (fullySelectedGroups.has(group.code)) {
          const childKeysInGroup = (group.specialties ?? []).map(s => groupSpecialtyKey(group.code, s.code));
          const childKeysSet = new Set(childKeysInGroup);
          if(!collapsed)
            result.push({
              content: formatSpecialtyWithCode(group),
              group: "Specialty",
              key: `specialty-group-${parentIdx++}`,
              onDismiss: () => setPendingSpecialtyCodes(pendingSpecialtyCodes.filter(k => !childKeysSet.has(k))),
              truncateAt: MAX_PILL_TEXT_LENGTH,
            });
        }
        for (const child of group.specialties ?? []) {
          const childKey = groupSpecialtyKey(group.code, child.code);
          if (!selectedKeys.has(childKey)) continue;
          const prefix = collapsed ? "" : `(${group.code}) `;
          result.push({
            content: `${prefix}${formatSpecialtyWithCode(child)}`,
            group: "Specialty",
            key: `specialty-${childIdx++}`,
            onDismiss: () => setPendingSpecialtyCodes(pendingSpecialtyCodes.filter(k => k !== childKey)),
            truncateAt: MAX_PILL_TEXT_LENGTH,
          });
        }
      }
    }

    if (pendingActionStatus && pendingActionStatus !== NetworkIntegrityActionStatus.All) {
      result.push({
        content: NetworkIntegrityActionStatusDisplay[pendingActionStatus],
        group: "Action Status",
        key: "action-status",
        onDismiss: () => setPendingActionStatus(NetworkIntegrityActionStatus.All),
        truncateAt: MAX_PILL_TEXT_LENGTH,
      });
    }

    if (pendingNpiType) {
      result.push({
        content: toNpiTypeLabel(pendingNpiType),
        group: "NPI Type",
        key: "npi-type",
        onDismiss: () => setPendingNpiType?.(null),
        truncateAt: MAX_PILL_TEXT_LENGTH,
      });
    }

    return result;
  }, [
    excessWarning,
    hasDualMembershipProviders,
    isDualPrimaryMembershipSelected,
    pendingActionStatus,
    pendingMembership,
    pendingNpiType,
    pendingProviderType,
    pendingRegionSetIds,
    pendingSpecialtyCodes,
    primaryMembershipAlias,
    secondaryMembershipAlias,
    serviceAreaOptions,
    setPendingActionStatus,
    setPendingNpiType,
    setPendingRegionSetIds,
    setPendingSpecialtyCodes,
    specialtyGroups,
    statusToRegionSet,
  ]);

  return (
    <QesActiveFiltersBar
      chips={chips}
      testIdPrefix="network-integrity__applied-filter"
      variant="accordion-style"
    />
  );
};

export default NetworkIntegrityAppliedFilters;
